import React, { useCallback, useMemo, useRef } from 'react';
import { Box, IconButton, Typography, useMediaQuery } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// ✅ PATH: file ada di src/Gallery => pakai ./Gallery/...
import B5_1 from './Gallery/B5-1.webp';
import B5_2 from './Gallery/B5-2.webp';
import B5_3 from './Gallery/B5-3.webp';
import B5_4 from './Gallery/B5-4.webp';

import B5D_1 from './Gallery/B5-Desktop-1.webp';
import B5D_2 from './Gallery/B5-Desktop-2.webp';
import B5D_3 from './Gallery/B5-Desktop-3.webp';
import B5D_4 from './Gallery/B5-Desktop-4.webp';

import KOTAK_1 from './Gallery/kotak-b5-1.webp';
import KOTAK_2 from './Gallery/kotak-b5-2.webp';
import KOTAK_3 from './Gallery/kotak-b5-3.webp';
import KOTAK_4 from './Gallery/kotak-b5-4.webp';

import KOTAK2_1 from './Gallery/kotak2-b5-1.webp';
import KOTAK2_2 from './Gallery/kotak2-b5-2.webp';
import KOTAK2_3 from './Gallery/kotak2-b5-3.webp';
import KOTAK2_4 from './Gallery/kotak2-b5-4.webp';

// ======================
// Small UI blocks
// ======================
function FullSection({ children, bg = '#000' }) {
  return (
    <Box
      sx={{
        position: 'relative',
        height: '100vh',
        width: '100%',
        overflow: 'hidden',
        backgroundColor: bg,
      }}
    >
      {children}
    </Box>
  );
}

function FullImage({ src, alt = '', fit = 'cover' }) {
  return (
    <Box
      component='img'
      src={src}
      alt={alt}
      loading='lazy'
      sx={{
        width: '100%',
        height: '100%',
        objectFit: fit,
        objectPosition: 'center',
        display: 'block',
      }}
    />
  );
}

function HorizontalCardsWithCopy({ heading, accentWord, items }) {
  const wrapRef = useRef(null);

  const scrollByDx = useCallback((dx) => {
    if (!wrapRef.current) return;
    wrapRef.current.scrollBy({ left: dx, behavior: 'smooth' });
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: '#f3efe7',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          flex: '0 0 auto',
          pt: { xs: 10, sm: 12 }, // buffer untuk header situs
          pb: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 5, md: 8 },
        }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            letterSpacing: 0.2,
            fontSize: { xs: '2rem', sm: '3rem', md: '3.8rem' },
            lineHeight: 1.02,
            color: '#0b0f17',
          }}
        >
          {heading}{' '}
          <Box component='span' sx={{ color: '#0b5bd3' }}>
            {accentWord}
          </Box>
        </Typography>
      </Box>

      {/* Cards Row */}
      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          position: 'relative',
          px: { xs: 1, sm: 2 },
          pb: { xs: 2, sm: 3 },
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* ✅ overlay controls layer (always on top) */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 30,
            pointerEvents: 'none',
          }}
        >
          <IconButton
            onClick={() => scrollByDx(-680)}
            sx={{
              position: 'absolute',
              left: { xs: 8, sm: 14 },
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'auto',
              backgroundColor: 'rgba(255,255,255,0.85)',
              boxShadow: '0 10px 22px rgba(0,0,0,0.12)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.98)' },
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>

          <IconButton
            onClick={() => scrollByDx(680)}
            sx={{
              position: 'absolute',
              right: { xs: 8, sm: 14 },
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'auto',
              backgroundColor: 'rgba(255,255,255,0.85)',
              boxShadow: '0 10px 22px rgba(0,0,0,0.12)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.98)' },
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>

        {/* track */}
        <Box
          ref={wrapRef}
          sx={{
            width: '100%',
            height: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            display: 'flex',
            alignItems: 'stretch',
            gap: { xs: 2, md: 3 },
            px: { xs: 2, sm: 5, md: 8 },
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollSnapType: 'x mandatory',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {items.map((it, idx) => (
            <Box
              key={idx}
              sx={{
                flex: '0 0 auto',

                // ✅ Presisi ukuran card: konsisten untuk semua item
                width: { xs: '86vw', sm: '72vw', md: '640px', lg: '720px' },

                // ✅ tinggi card menyesuaikan area; tidak ketiban bawah
                height: '100%',

                scrollSnapAlign: 'start',
                display: 'grid',
                gridTemplateRows: {
                  xs: '56vh auto',
                  sm: '58vh auto',
                  md: '60vh auto',
                }, // image + text
                borderRadius: { xs: 4, md: 6 },
                overflow: 'hidden',
                backgroundColor: '#f3efe7',
              }}
            >
              {/* image */}
              <Box
                sx={{
                  borderRadius: { xs: 4, md: 6 },
                  overflow: 'hidden',
                  backgroundColor: '#fff',
                  boxShadow: '0 14px 40px rgba(0,0,0,0.16)',
                }}
              >
                <Box
                  component='img'
                  src={it.image}
                  alt={it.title}
                  loading='lazy'
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </Box>

              {/* text */}
              <Box
                sx={{
                  pt: { xs: 1.4, sm: 1.6, md: 2 },
                  px: { xs: 0.5, sm: 0.5 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    fontWeight: 700,
                    color: 'rgba(11,15,23,0.65)',
                    letterSpacing: 0.2,
                  }}
                >
                  {it.kicker}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.6,
                    fontSize: { xs: '1.65rem', sm: '2.1rem', md: '2.6rem' },
                    fontWeight: 900,
                    lineHeight: 1.05,
                    color: '#0b0f17',
                  }}
                >
                  {it.title}
                </Typography>

                <Typography
                  sx={{
                    mt: 1,
                    maxWidth: { xs: '92%', sm: '90%', md: '88%' },
                    fontSize: { xs: '0.98rem', sm: '1.05rem' },
                    lineHeight: 1.55,
                    color: 'rgba(11,15,23,0.78)',
                  }}
                >
                  {it.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ======================
// Page
// ======================
export default function JelajahiB5() {
  // ✅ auto switch mobile/desktop
  const isMobile = useMediaQuery('(max-width:900px)');

  // ✅ pilih assets sesuai device
  const sec1 = isMobile ? B5_1 : B5D_1;
  const sec2 = isMobile ? B5_2 : B5D_2;
  const sec3 = isMobile ? B5_3 : B5D_3;
  const sec4 = isMobile ? B5_4 : B5D_4;

  const hero1Fit = isMobile ? 'cover' : 'cover';

  const kotak1Items = useMemo(
    () => [
      {
        image: KOTAK_1,
        kicker: 'Kenyamanan yang Halus',
        title: 'Dibuat untuk Perjalanan Jarak Jauh',
        desc: 'Dirancang untuk jarak tempuh panjang, kabin B5 menjaga pengemudi tetap nyaman. Kursi yang terbentuk presisi, material premium, serta posisi berkendara yang intuitif membantu mengurangi kelelahan saat perjalanan jauh.',
      },
      {
        image: KOTAK_2,
        kicker: 'Lebih dari Sekadar Suspensi',
        title: 'DiSus-P Body Control System',
        desc: 'DiSus-P bukan hanya suspensi, tetapi sistem kontrol bodi terintegrasi. Dengan ride height yang dapat disetel, stabilitas meningkat, dan redaman adaptif, B5 tetap percaya diri di jalan mulus maupun medan yang tidak rata.',
      },
      {
        image: KOTAK_3,
        kicker: 'Mechanical Diff-Locks',
        title: 'Torsi Instan. Traksi Maksimum.',
        desc: 'Differential lock elektrik di kedua gardan membantu menyalurkan torsi secara merata saat traksi menurun. Saat melintasi ruts, washouts, atau permukaan longgar, kendaraan tetap stabil dan konsisten.',
      },
      {
        image: KOTAK_4,
        kicker: 'Body-on-Frame Architecture',
        title: 'Rigid, Safe, and Built for Every Surface',
        desc: 'Rangka body-on-frame memaksimalkan kekakuan torsional untuk handling lebih solid dan kemampuan towing lebih tinggi. Diperkuat teknologi Cell-to-Chassis, struktur lebih aman dan siap menghadapi permukaan yang tidak terduga.',
      },
    ],
    [],
  );

  const kotak2Items = useMemo(
    () => [
      {
        image: KOTAK2_1,
        kicker: 'Interior',
        title: 'Excellence',
        desc: 'B5 mengintegrasikan asisten suara AI empat zona yang dapat mengontrol navigasi, musik, dan fitur lain dengan mulus dari kursi mana pun di kabin.',
      },
      {
        image: KOTAK2_2,
        kicker: 'Advanced Driver Assistance Systems',
        title: 'Omnidirectional Protection',
        desc: 'Salah satu sistem keselamatan paling komprehensif di kelasnya. B5 menawarkan perlindungan 360°, bukan sekadar kamera 360°. Teknologi keselamatan aktif dan pasif dipadukan dengan lokalisasi ADAS untuk kondisi jalan, sehingga kendaraan selalu “membaca” situasi di depan.',
      },
      {
        image: KOTAK2_3,
        kicker: 'Elegance Meets',
        title: 'Intelligence',
        desc: 'Blade Battery termasuk baterai paling aman di dunia. Ditempatkan dalam struktur proteksi berlapis dan terintegrasi ke struktur kendaraan melalui desain Cell-to-Chassis (CTC), menghasilkan rigiditas dan kekuatan yang sangat tinggi.',
      },
      {
        image: KOTAK2_4,
        kicker: 'Your DENZA Smartkey',
        title: 'Key Your Way',
        desc: 'DENZA B5 memberi pilihan akses yang fleksibel: key fob dengan proximity sensing, NFC keycard fisik, hingga Digital Key di smartphone. Bahkan mendukung kompatibilitas UWB untuk membuka saat mendekat dan mengunci saat Anda menjauh.',
      },
    ],
    [],
  );

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {/* 1) B5-1 (full 100vh) + overlay text */}
      <FullSection>
        <FullImage src={sec1} alt='b5-1' fit={hero1Fit} />

        {/* overlay */}
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {/* top-left heading */}
          <Box
            sx={{
              position: 'absolute',
              top: { xs: 'calc(64px + 14px)', sm: 'calc(72px + 18px)' },
              left: { xs: 16, sm: 40 },
              maxWidth: { xs: '92%', sm: '60%' },
              color: '#fff',
              textShadow: '0 6px 18px rgba(0,0,0,0.55)',
            }}
          >
            <Typography
              sx={{
                fontWeight: 900,
                letterSpacing: 0.3,
                fontSize: { xs: '1.6rem', sm: '2.3rem', md: '3rem' },
                lineHeight: 1.05,
              }}
            >
              Denza B5
            </Typography>

            <Typography
              sx={{
                mt: 1,
                fontWeight: 600,
                fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.15rem' },
                opacity: 0.95,
              }}
            >
              Akselerasi dan torsi instan untuk respons yang agresif dan halus.
            </Typography>
          </Box>

          {/* bottom specs bar */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: { xs: 18, sm: 22 },
              px: { xs: 2, sm: 5, md: 8 },
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
              gap: { xs: 1.2, sm: 2 },
              color: '#fff',
              textShadow: '0 6px 18px rgba(0,0,0,0.55)',
            }}
          >
            {[
              { value: '0–100 km/jam', label: '4,8 detik' },
              { value: 'DM-O', label: 'Teknologi Super Hybrid' },
              { value: '400 kW / 760 Nm', label: 'Daya dan Torsi Maksimum' },
              { value: '90 km WLTC', label: 'Mode Listrik Penuh' },
            ].map((item, i) => (
              <Box
                key={i}
                sx={{
                  borderRadius: 2.5,
                  px: { xs: 1.2, sm: 1.5 },
                  py: { xs: 1, sm: 1.2 },
                  background: 'rgba(0,0,0,0.28)',
                  backdropFilter: 'blur(6px)',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: '1.05rem', sm: '1.2rem', md: '1.35rem' },
                    lineHeight: 1.1,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.3,
                    fontWeight: 600,
                    fontSize: { xs: '0.78rem', sm: '0.85rem', md: '0.9rem' },
                    opacity: 0.95,
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </FullSection>

      {/* 2) B5-2 (full 100vh) */}
      <FullSection>
        <FullImage src={sec2} alt='b5-2' fit={hero1Fit} />
      </FullSection>

      {/* 3) kotak-b5-1..4 (full 100vh) + heading */}
      <FullSection bg='#f3efe7'>
        <HorizontalCardsWithCopy
          heading='Electrifying'
          accentWord='Performance'
          items={kotak1Items}
        />
      </FullSection>

      {/* 4) B5-3 (full 100vh) + “EXPERIENCE THE DENZA DIFFERENCE” + desc Indonesia */}
      <FullSection>
        <FullImage src={sec3} alt='b5-3' fit='cover' />

        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <Box
            sx={{
              position: 'absolute',
              top: { xs: 'calc(64px + 26px)', sm: 'calc(72px + 32px)' },
              left: { xs: 16, sm: 48, md: 70 },
              color: '#fff',
              textShadow: '0 10px 26px rgba(0,0,0,0.55)',
            }}
          >
            <Typography
              sx={{
                fontWeight: 900,
                letterSpacing: 2,
                fontSize: { xs: '2.1rem', sm: '3.2rem', md: '4.4rem' },
                lineHeight: 1,
                color: '#0b5bd3',
              }}
            >
              EXPERIENCE
            </Typography>
            <Typography
              sx={{
                mt: 0.8,
                fontWeight: 900,
                letterSpacing: 2,
                fontSize: { xs: '2.2rem', sm: '3.4rem', md: '4.6rem' },
                lineHeight: 1,
              }}
            >
              THE DENZA
            </Typography>
            <Typography
              sx={{
                mt: 0.8,
                fontWeight: 900,
                letterSpacing: 2,
                fontSize: { xs: '2.2rem', sm: '3.4rem', md: '4.6rem' },
                lineHeight: 1,
              }}
            >
              DIFFERENCE
            </Typography>
          </Box>

          <Box
            sx={{
              position: 'absolute',
              left: { xs: 16, sm: 48, md: 70 },
              bottom: { xs: 90, sm: 70 },
              maxWidth: { xs: '92%', sm: '70%', md: '52%' },
              color: 'rgba(255,255,255,0.92)',
              textShadow: '0 10px 26px rgba(0,0,0,0.55)',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              lineHeight: 1.6,
            }}
          >
            Untuk waktu yang lama, dunia off-road didominasi mesin diesel yang
            bising, kotor, dan terasa lambat. DENZA B5 hadir sebagai
            interpretasi baru untuk petualangan ke mana saja—lebih senyap,
            responsif, dan effortless. Saat ketenangan jadi raja, performa
            terasa ringan, dan alam terbuka menyambut Anda sepenuh hati. Selamat
            datang di DENZA difference.
          </Box>
        </Box>
      </FullSection>

      {/* 5) kotak2-b5-1..4 (full 100vh) + heading */}
      <FullSection bg='#f3efe7'>
        <HorizontalCardsWithCopy
          heading='Interior'
          accentWord='Excellence'
          items={kotak2Items}
        />
      </FullSection>

      {/* 6) B5-4 (full 100vh) + last copy */}
      <FullSection>
        <FullImage src={sec4} alt='b5-4' fit='cover' />

        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <Box
            sx={{
              position: 'absolute',
              left: { xs: 16, sm: 48, md: 70 },
              bottom: { xs: 70, sm: 85 },
              color: '#fff',
              textShadow: '0 10px 26px rgba(0,0,0,0.55)',
              maxWidth: { xs: '92%', sm: '70%', md: '55%' },
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                opacity: 0.9,
              }}
            >
              All-new
            </Typography>
            <Typography
              sx={{
                mt: 0.6,
                fontWeight: 900,
                letterSpacing: 0.5,
                fontSize: { xs: '2.2rem', sm: '3rem', md: '3.6rem' },
                lineHeight: 1.05,
              }}
            >
              DENZA B5 4X4
            </Typography>
            <Typography
              sx={{
                mt: 1.2,
                fontWeight: 600,
                fontSize: { xs: '1rem', sm: '1.1rem' },
                lineHeight: 1.55,
                opacity: 0.95,
              }}
            >
              Be among the first to experience a new chapter in off-roading.
            </Typography>
          </Box>
        </Box>
      </FullSection>
    </Box>
  );
}
